import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalAdminLayout from "./PortalAdminLayout";

/**
 * 22J — SUPPORT REVIEWS A FREEZE (user, 2026-10-09: *"let the sender be able to freeze and the support backend
 * have the ability to cancel, can you put the support part in a later phase, preferably last before final
 * testing"*).
 *
 * ── WHAT THIS SCREEN IS FOR ─────────────────────────────────────────────────────────────────────
 *
 * A sender who suspects a transfer was not theirs can stop a package immediately, and they cannot undo it — that
 * asymmetry is deliberate, because the person alleging a bad transfer must not decide the dispute. So a freeze
 * is a REQUEST, and this is where a human answers it. Every freeze that is never answered is a package frozen
 * for ever, which is why the queue and the decision live on one screen rather than in a ticket system.
 *
 * ── WHY THE DECISION STARTS FROM THE RECORD ────────────────────────────────────────────────────
 *
 * The purchase already says when it was frozen, by which address, why, and what it was before. The reviewer
 * reads that rather than a memory of it. What they are shown is deliberately the WHOLE thing: who holds it now,
 * who gave it away, what it is worth, how many sessions are left and what has already been booked — because a
 * cancel is not a reversal of the money, and the reviewer has to be able to see that before choosing it.
 *
 * ── WHY THERE ARE THREE BUTTONS AND NOT A STATUS DROPDOWN ──────────────────────────────────────
 *
 * Each outcome does something different to three different parties. A dropdown says "pick a value"; these say
 * what happens, and each one asks first. The cancel confirmation names the money explicitly, because "cancelled"
 * and "refunded" are not the same word and a customer reading silence will assume the second.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const money = (amount, currency) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch (_error) {
    return `$${value.toFixed(2)}`;
  }
};

const when = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const OUTCOMES = [
  {
    key: "release",
    label: "Release the package",
    tone: "#047857",
    background: "#ecfdf5",
    border: "#10b981",
    explain:
      "The move stands and the holder can use it again. Choose this when the transfer looks genuine.",
  },
  {
    key: "cancel",
    label: "Cancel the package",
    tone: "#b91c1c",
    background: "#fef2f2",
    border: "#ef4444",
    explain:
      "The package ends for good and unused sessions stop being owed. This does NOT refund anything — money already paid out is a separate decision.",
  },
  {
    key: "return_to_sender",
    label: "Return to the sender",
    tone: "#92400e",
    background: "#fffbeb",
    border: "#f59e0b",
    explain:
      "The package goes back to the address that held it, under a new number. The holder is told why.",
  },
];

export default function PackageFreezeReviews() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadList = useCallback(async () => {
    const response = await fetch(`${API_BASE}/api/packages/support/frozen`, {
      headers: authHeaders(),
    });
    if (response.status === 401 || response.status === 403) {
      navigate("/login");
      return;
    }
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.message || "Could not load frozen packages.");
    }
    setList(payload.frozen || []);
  }, [authHeaders, navigate]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await loadList();
      } catch (loadError) {
        if (alive) setError(loadError.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadList]);

  const openOne = async (purchaseId) => {
    setSelected(purchaseId);
    setDetail(null);
    setError("");
    setMessage("");
    setNote("");
    try {
      const response = await fetch(
        `${API_BASE}/api/packages/support/frozen/${purchaseId}`,
        { headers: authHeaders() },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Could not load that package.");
      }
      setDetail(payload);
    } catch (loadError) {
      setError(loadError.message);
    }
  };

  const decide = async (outcome) => {
    if (!detail?.purchase) return;
    const purchase = detail.purchase;

    // EACH ONE ASKS FIRST, in its own words. A single "are you sure?" would leave the reviewer agreeing to
    // something they had not read, which is how a package gets cancelled by a slip of the mouse.
    const confirmations = {
      release: `Release "${purchase.packageName}" back to ${purchase.holderEmail}? The transfer stands.`,
      cancel: `Cancel "${purchase.packageName}" for good? Unused sessions will not be owed, and NOTHING will be refunded by this decision.`,
      return_to_sender: `Return "${purchase.packageName}" to ${purchase.senderEmail}? It gets a new number and the current holder (${purchase.holderEmail}) is told why.`,
    };
    if (!window.confirm(confirmations[outcome])) return;

    setBusy(outcome);
    setError("");
    setMessage("");
    try {
      const response = await fetch(
        `${API_BASE}/api/packages/support/frozen/${purchase.purchaseId}/resolve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ outcome, note: note.trim() || null }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Could not record that decision.");
      }
      setMessage(payload.message || "Decision recorded.");
      // THE QUEUE MOVES WITH THE DECISION. Leaving a resolved package in the list would put it back in front of
      // the next reviewer, who would then be told it was already decided.
      setList((current) => current.filter((row) => row.purchaseId !== purchase.purchaseId));
      setSelected(null);
      setDetail(null);
      setNote("");
    } catch (decideError) {
      setError(decideError.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    // THE PAGE OWNS ITS LAYOUT (reported 2026-10-19: *"the frozen packages page is orphaned, no menu"*). Every
    // console in this folder wraps itself in `PortalAdminLayout` — the portal sidebar lives there, not in the
    // route — so a page that returns its own markup renders with no navigation at all and no way back.
    <PortalAdminLayout>
    <div style={{ padding: "24px", maxWidth: "1180px", margin: "0 auto" }}>
      <h1 style={{ fontSize: "24px", margin: "0 0 6px", color: "#1f2a44" }}>
        Frozen packages
      </h1>
      <p style={{ color: "#5b6475", margin: "0 0 18px", fontSize: "14px" }}>
        A sender froze these while we look into how they changed hands. Decide each one:
        release it, cancel it, or return it to the sender.
      </p>

      {message && (
        <p
          style={{
            background: "#ecfdf5",
            border: "1px solid #10b981",
            color: "#065f46",
            borderRadius: "8px",
            padding: "10px 14px",
            fontSize: "14px",
          }}
        >
          {message}
        </p>
      )}
      {error && (
        <p
          style={{
            background: "#fef2f2",
            border: "1px solid #ef4444",
            color: "#991b1b",
            borderRadius: "8px",
            padding: "10px 14px",
            fontSize: "14px",
          }}
        >
          {error}
        </p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 420px) 1fr", gap: "18px" }}>
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 14px",
              borderBottom: "1px solid #eef1f6",
              fontSize: "13px",
              fontWeight: 700,
              color: "#374151",
            }}
          >
            Waiting for review ({list.length})
          </div>
          {loading ? (
            <p style={{ padding: "14px", color: "#6b7280", fontSize: "14px" }}>Loading…</p>
          ) : list.length === 0 ? (
            <p style={{ padding: "14px", color: "#6b7280", fontSize: "14px" }}>
              Nothing is frozen. Every report has been decided.
            </p>
          ) : (
            list.map((row) => (
              <button
                key={row.purchaseId}
                type="button"
                onClick={() => openOne(row.purchaseId)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "12px 14px",
                  border: "none",
                  borderBottom: "1px solid #f1f5f9",
                  background:
                    selected === row.purchaseId ? "#eff6ff" : "transparent",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#1f2a44" }}>
                  {row.packageName}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  {row.shopName || "Shop"} · held by {row.holderEmail || "—"}
                </div>
                <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                  Frozen {when(row.frozenAt)}
                </div>
              </button>
            ))
          )}
        </div>

        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "18px",
            minHeight: "240px",
          }}
        >
          {!detail?.purchase ? (
            <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
              Pick a package to see the whole record before deciding.
            </p>
          ) : (
            <ReviewPanel
              purchase={detail.purchase}
              reviews={detail.reviews || []}
              note={note}
              onNote={setNote}
              busy={busy}
              onDecide={decide}
            />
          )}
        </div>
      </div>
    </div>
    </PortalAdminLayout>
  );
}

function Row({ label, value, tone }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "12px",
        padding: "7px 0",
        borderBottom: "1px solid #eef1f6",
        fontSize: "14px",
      }}
    >
      <span style={{ color: "#5b6475" }}>{label}</span>
      <span
        style={{
          color: tone === "danger" ? "#c0392b" : "#1f2a44",
          fontWeight: 600,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ReviewPanel({ purchase, reviews, note, onNote, busy, onDecide }) {
  return (
    <div>
      <h2 style={{ margin: "0 0 4px", fontSize: "18px", color: "#1f2a44" }}>
        {purchase.packageName}
      </h2>
      <p style={{ margin: "0 0 14px", fontSize: "13px", color: "#6b7280" }}>
        {purchase.shopName || "Shop"} ·{" "}
        {purchase.accountNumber
          ? `package number ${String(purchase.accountNumber).replace(/(\d{4})(?=\d)/g, "$1 ")}`
          : "no number on file"}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
        <div>
          <Row label="Held by" value={purchase.holderEmail || "—"} />
          <Row label="Holder name" value={purchase.holderName || "—"} />
          <Row label="Given by" value={purchase.senderEmail || "—"} />
          <Row label="Sender name" value={purchase.senderName || "—"} />
          <Row
            label="Frozen"
            value={when(purchase.frozenAt)}
          />
          <Row label="Frozen by" value={purchase.frozenByEmail || "—"} />
          <Row label="Reason given" value={purchase.frozenReason || "—"} />
        </div>
        <div>
          <Row
            label="State before the freeze"
            value={purchase.statusBeforeFreeze || "—"}
          />
          <Row
            label="Sessions"
            value={`${purchase.sessions.remaining} left of ${purchase.sessions.total} (${purchase.sessions.used} used)`}
          />
          <Row label="Valid until" value={when(purchase.validUntil)} />
          <Row label="Booked sessions" value={String(purchase.bookingsCount)} />
          <Row
            label="Price paid"
            value={money(purchase.pricePaid, purchase.currency)}
          />
          <Row label="Purchased" value={when(purchase.purchaseDate)} />
        </div>
      </div>

      {purchase.ownershipHistory?.length > 0 && (
        <div style={{ marginTop: "14px" }}>
          <h3 style={{ fontSize: "14px", margin: "0 0 6px", color: "#374151" }}>
            How it changed hands
          </h3>
          {purchase.ownershipHistory.map((entry, index) => (
            <p
              key={`${entry.toEmail}-${index}`}
              style={{ margin: "0 0 4px", fontSize: "13px", color: "#4b5563" }}
            >
              {entry.fromEmail || "—"} → {entry.toEmail || "—"} on{" "}
              {when(entry.transferredAt)}
              {entry.rotatedFromAccountNumber
                ? ` · old number ${entry.rotatedFromAccountNumber} retired`
                : ""}
            </p>
          ))}
        </div>
      )}

      <label
        style={{
          display: "block",
          marginTop: "16px",
          fontSize: "13px",
          fontWeight: 600,
          color: "#374151",
        }}
      >
        Note for the record (optional)
        <textarea
          value={note}
          onChange={(event) => onNote(event.target.value)}
          rows={2}
          style={{
            display: "block",
            width: "100%",
            marginTop: "6px",
            padding: "9px 10px",
            borderRadius: "8px",
            border: "1px solid #d6dbe6",
            fontSize: "14px",
            fontFamily: "inherit",
            resize: "vertical",
          }}
        />
      </label>

      <div style={{ display: "grid", gap: "10px", marginTop: "16px" }}>
        {OUTCOMES.map((outcome) => (
          <div
            key={outcome.key}
            style={{
              border: `1px solid ${outcome.border}`,
              background: outcome.background,
              borderRadius: "10px",
              padding: "12px 14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "14px",
            }}
          >
            <div>
              <div style={{ fontSize: "14px", fontWeight: 700, color: outcome.tone }}>
                {outcome.label}
              </div>
              <div style={{ fontSize: "12.5px", color: "#4b5563", marginTop: "2px" }}>
                {outcome.explain}
              </div>
            </div>
            <button
              type="button"
              disabled={Boolean(busy) || (outcome.key === "return_to_sender" && !purchase.canReturnToSender)}
              onClick={() => onDecide(outcome.key)}
              style={{
                flex: "0 0 auto",
                padding: "9px 14px",
                borderRadius: "8px",
                border: `1px solid ${outcome.border}`,
                background: "#ffffff",
                color: outcome.tone,
                fontWeight: 700,
                fontSize: "13px",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy === outcome.key ? "Saving…" : outcome.label}
            </button>
          </div>
        ))}
        {!purchase.canReturnToSender && (
          <p style={{ margin: 0, fontSize: "12.5px", color: "#9ca3af" }}>
            Return to sender is unavailable: this package has no recorded sender, so it was
            not transferred.
          </p>
        )}
      </div>

      {reviews.length > 0 && (
        <div style={{ marginTop: "18px" }}>
          <h3 style={{ fontSize: "14px", margin: "0 0 6px", color: "#374151" }}>
            Decisions already made
          </h3>
          {reviews.map((review, index) => (
            <p
              key={`${review.at}-${index}`}
              style={{ margin: "0 0 4px", fontSize: "13px", color: "#4b5563" }}
            >
              <strong>{review.outcome}</strong> by {review.reviewerEmail || "—"} on{" "}
              {when(review.at)}
              {review.note ? ` — “${review.note}”` : ""}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
